// Everything the site tells Google Tag Manager goes through here.
//
// One push function, one place to stop personal data leaving the browser. A new
// event means one call in the component and a tag in GTM — no plumbing to add.
// Keys that would carry somebody's details. Matching is deliberate rather than
// "contains the word name": `step_name`, `file_name` and `event_name` are
// descriptions of what happened, not descriptions of a person.
const PERSONAL_EXACT = new Set([
  "name", "names", "address", "message", "notes", "company", "business",
  "dob", "password", "postcode", "post_code", "zip", "telephone", "mobile",
]);
const PERSONAL_PATTERN = /(email|e_mail|phone|mobile_number|postcode|password|\bdob\b)/i;
const PERSONAL_NAME_PATTERN = /(^|_)(first|last|full|given|family|contact|client|invitee|customer|company|business|user|person|lead)_?names?($|_)/i;
const PERSONAL_ADDRESS_PATTERN = /(^|_)(street|billing|shipping|home|postal)_?address($|_)|(^|_)address($|_)/i;
const MAX_STRING = 120;

function isPersonal(key) {
  const k = key.toLowerCase();
  return (
    PERSONAL_EXACT.has(k) ||
    PERSONAL_PATTERN.test(k) ||
    PERSONAL_NAME_PATTERN.test(k) ||
    PERSONAL_ADDRESS_PATTERN.test(k)
  );
}

function clean(params = {}) {
  const out = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    // A parameter that would carry somebody's details never leaves the page,
    // whatever a caller passes. GA4 must not hold personal data.
    if (isPersonal(key)) continue;
    if (typeof value === "string") out[key] = value.slice(0, MAX_STRING);
    else if (typeof value === "number" || typeof value === "boolean") out[key] = value;
  }
  return out;
}

// Last push of each event, so the same action can't be recorded twice.
//
// A remount, a listener attached twice, a double-fired handler or a visitor
// double-clicking all produce identical pushes a moment apart. GA4 has no way to
// tell those from two genuine actions, so they are stopped here at the source
// rather than filtered inside Tag Manager. Two real actions of the same kind a
// couple of seconds apart are vanishingly rare; a duplicate within that window
// is almost always an accident.
const DEDUPE_MS = 2000;
const lastPush = new Map();

export function pushToDataLayer(event, params = {}, options = {}) {
  if (typeof window === "undefined") return false;
  const payload = clean(params);
  const fingerprint = `${event}|${JSON.stringify(payload)}`;
  const now = Date.now();
  const previous = lastPush.get(fingerprint);
  if (previous && now - previous < DEDUPE_MS) return false;
  lastPush.set(fingerprint, now);
  window.dataLayer = window.dataLayer || [];
  // eventCallback is Tag Manager's own acknowledgement that it processed this
  // event and dispatched the tags that matched it. The push itself proves
  // nothing — the array accepts anything, with or without a container listening.
  const extras = {};
  if (typeof options.onProcessed === "function") {
    extras.eventCallback = options.onProcessed;
    extras.eventTimeout = options.timeoutMs || 3000;
  }
  window.dataLayer.push({ event, ...payload, ...extras });
  return true;
}

// Whether a container is actually on the page. Tag Manager defines this as soon
// as it loads, so a push with no container behind it can be told apart from one
// that was genuinely handled.
export function tagManagerLoaded() {
  return typeof window !== "undefined" && Boolean(window.google_tag_manager);
}

// The GA4 client id, read from the cookie Google sets, so a booking confirmed on
// the server later can be joined to the same visitor rather than looking like a
// stranger. Empty until analytics consent has been given, which is correct.
export function gaClientId() {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(/_ga=GA\d\.\d\.(\d+\.\d+)/);
  return match ? match[1] : "";
}
